<template>
  <div class="user-tab-overview">

    
    <v-row v-if="studiesLoading" class="mt-4 mb-4">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-2 grey--text">Fetching project data from Database/Polynom...</div>
      </v-col>
    </v-row>
    <div 
      v-else>
    
    <v-form ref="normsForm">
    <!-- Global Project Norms -->
    <v-card class="mb-7" elevation="2" outlined>
      <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
        Project - Norms
      </v-card-title>
      <v-card-text class="pt-4">
          <v-row dense>
            <div class="text-overline grey--text">Was the pack validated?</div>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="projectDataLocal.validation"
                item-text="label"
                outlined dense hide-details="auto"
              ></v-checkbox>
            </v-col>
          </v-row>
      </v-card-text>
    </v-card>


    <!-- Country Iteration -->
    <v-card 
      v-for="(country, cIdx) in projectDataLocal.countries" 
      :key="country.id" 
      class="mb-9 elevation-1"
    >
      <v-toolbar flat color="primary" dark dense>
        <v-icon left small>{{ icons.mdiEarth }}</v-icon>
        <v-toolbar-title class="body-1 font-weight-bold">
          {{ country.name }} - Country Settings
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn small color="white" outlined @click="openDatamapper(country.id)">
          <v-icon left small>{{ icons.mdiDatabaseSync }}</v-icon> Datamapper
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-6">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="country.retail_channel" :items="options.retail_channel" :loading="optionsLoading.retail_channel" item-text="label" label="Retail Channel" outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="4">
             <v-autocomplete v-model="country.brand_types" :items="options.brand_types" :loading="optionsLoading.brand_types" item-text="label" label="Brand Type" multiple outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="country.findability" :items="options.findabilities" :loading="optionsLoading.findabilities" item-text="label" label="Findabilities" outlined dense hide-details="auto" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
             <v-autocomplete v-model="country.data_collection" :items="options.data_collections" :loading="optionsLoading.data_collections" item-text="label" label="Data Collection" outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="6">
             <v-autocomplete v-model="country.flash_exp" :items="options.flash_exposures" :loading="optionsLoading.flash_exposures" item-text="label" label="Flash Exposures" outlined dense hide-details="auto" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
             <v-autocomplete v-model="country.communication" :items="options.communications" :loading="optionsLoading.communications" item-text="label" label="Communications" outlined dense hide-details="auto" />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete v-model="country.project_outcome" :items="options.project_outcomes" :loading="optionsLoading.project_outcomes" item-text="label" label="Project Outcomes" outlined dense hide-details="auto" />
          </v-col>
        </v-row>
        
        <v-row dense>
            <v-col cols="12" md="12">
              <v-autocomplete
                v-model="country.client_objectives"
                :loading="optionsLoading.client_objectives"
                :items="options.client_objectives"
                :rules="[rules.requiredArray]"
                item-text="label"
                label="Client Objectives"
                multiple small-chips deletable-chips outlined dense hide-details="auto"
              ></v-autocomplete>
            </v-col>
        </v-row>

        <!-- Phases / Cells Section -->
        <div class="mt-8">
          <div class="d-flex align-center mb-3">
            <div class="text-overline grey--text">Phases / Cells Configuration</div>
            <v-spacer></v-spacer>
            
            <v-btn x-small color="primary" outlined @click="copyFirstPhaseToAll(country)" v-if="country.phases && country.phases.length > 1">
              <v-icon x-small left>{{ icons.mdiContentCopy }}</v-icon> Copy Phase 1 to All
            </v-btn>

            <v-btn x-small color="success" outlined class="ml-3" 
              :loading="addingPhaseId === country.id" 
              @click="addPhase(country)">
              <v-icon x-small left>{{ icons.mdiPlus }}</v-icon> Add Phase
            </v-btn>
          </div>
          
          <v-row v-if="country.phases && country.phases.length">
            <v-col 
              v-for="(phase, pIdx) in country.phases" 
              :key="phase.cell_id" 
              cols="12" md="6" lg="4"
            >
              <v-card outlined class="phase-card overflow-hidden">
                <div class="pa-3 grey lighten-5 border-bottom d-flex align-center">
                  <div class="flex-grow-1">
                    <div class="d-flex align-center">
                       <!-- Editable Cell Name -->
                       <v-text-field 
                          v-model="phase.cell_name" 
                          dense 
                          hide-details 
                          outlined 
                          class="font-weight-black mr-2 text-subtitle-1 bg-white"
                          style="max-width: 200px;"
                          placeholder="Cell Name"
                        ></v-text-field>
                    </div>
                  </div>
                  <v-chip x-small :color="statusColor(phase.cell_status)" dark>
                    {{ phase.cell_status }}
                  </v-chip>
                </div>

                <v-card-text class="pa-4">
                  <div class="d-flex align-start mb-6">
                    <v-hover v-slot="{ hover }">
                      <v-avatar 
                        size="80" 
                        rounded 
                        color="grey lighten-4" 
                        class="mr-4 elevation-1 border"
                        style="cursor: pointer;"
                        @click="openImageModal(phase.picture_path)"
                      >
                        <v-img v-if="phase.picture_path" :src="phase.picture_path" contain>
                          <v-fade-transition v-if="hover">
                            <div class="d-flex align-center justify-center transition-fast-in-fast-out v-card--reveal white--text text-caption" style="height: 100%; background: rgba(0,0,0,0.5)">
                              View Image
                            </div>
                          </v-fade-transition>
                        </v-img>
                        <v-icon v-else color="grey" large>{{ icons.mdiPackageVariant }}</v-icon>
                      </v-avatar>
                    </v-hover>
                    <div>
                    <!-- Editable Design Type -->
                    <v-select
                      v-model="phase.design_type"
                      :items="['Current', 'Test']" 
                      label="Design Type"
                      dense
                      outlined
                      hide-details
                      style="max-width: 150px;"
                    ></v-select>
                    
                  <!-- Shows Decipher DB ID if it exists -->
                  <v-text-field
                    v-model="phase.db_id_pack"
                    label="Phase # Decipher"
                    dense
                    outlined
                    :rules="[...rules.decipherPhaseRules(country, phase)]"
                    hide-details="auto"
                    class="mt-2">
                  </v-text-field>
                  </div>   
                  </div>

                  <v-row dense class="mb-4">
                    <v-col cols="12">
                       <v-autocomplete v-model="phase.pack_change" :items="options.pack_change" item-text="label" label="Pack Change Type" multiple small-chips outlined dense hide-details="auto"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" class="mt-2">
                       <v-autocomplete v-model="phase.sample_types" :items="options.sample_types" item-text="label" label="Phase Sample Type" multiple small-chips outlined dense hide-details="auto"></v-autocomplete>
                    </v-col>
                  </v-row>

                  <div class="text-caption font-weight-bold grey--text text-uppercase mb-2" style="letter-spacing: 1px">Quantities & Size</div>
                  <v-row dense class="mb-4">
                    <v-col cols="6">
                      <v-text-field v-model="phase.range_tested" label="Range Tested" type="number" outlined dense hide-details="auto" :prepend-inner-icon="icons.mdiFormatListNumbered" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="phase.total_products" label="Total Prods" type="number" outlined dense hide-details="auto" :prepend-inner-icon="icons.mdiPackageVariant" />
                    </v-col>
                    <v-col cols="12" class="mt-2">
                       <v-text-field v-model="phase.shelf_size" label="Shelf Size (m)" type="number" outlined dense hide-details="auto" suffix="meters" />
                    </v-col>
                  </v-row>

                  <div class="text-caption font-weight-bold grey--text text-uppercase mb-2" style="letter-spacing: 1px">Shelf Configuration</div>
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field v-model="phase.share_of_shelf" label="Share of Shelf" type="number" suffix="%" :prepend-inner-icon="icons.mdiChartPie" outlined dense hide-details="auto" class="mb-2" />
                    </v-col>
                    <v-col cols="7">
                      <v-select v-model="phase.product_position" :items="options.product_positions" item-text="label" label="Position" outlined dense hide-details="auto" />
                    </v-col>
                    <v-col cols="5" class="d-flex align-center">
                       <v-checkbox v-model="phase.product_location" label="Fixed" class="mt-0 pt-0 ml-2" hide-details />
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions class="pa-2 grey lighten-5">
                  <v-btn text x-small color="grey darken-1" v-if="!phase.cell_id.startsWith('temp-')">
                    ID: {{ phase.cell_id.substring(0,8) }}-{{ phase.cell_id.split('-').pop() }}
                  </v-btn>
                  <v-chip x-small color="info" outlined v-else>Unsaved New Phase</v-chip>
                  
                  <v-spacer></v-spacer>
                  <v-btn icon color="error" small @click="confirmDeleteCell(country, phase)">
                    <v-icon small>{{ icons.mdiDelete }}</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <div v-else class="text-center py-4 grey--text">
            No phases configured for this country yet. Click "Add Phase" to start.
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    </v-form>
    <v-footer app inset color="white" class="elevation-10 pa-4">
      <v-spacer></v-spacer>
      <v-btn small outlined color="secondary" class="mr-4" @click="revertToInitialData">Discard Changes</v-btn>
      <v-btn small color="primary" class="mr-14" :loading="isSaving" @click.prevent="onSubmit" px-10>
        Update Project Settings
      </v-btn>
    </v-footer>
  </div>
    

    <!-- Global Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon color="error" class="mr-2">{{ icons.mdiDelete }}</v-icon> Delete Cell?
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete <strong>{{ cellToDelete }}</strong>?
          This action will permanently remove it from both the local database and the Polynom API.
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="deleteDialog = false" :disabled="isDeleting">Cancel</v-btn>
          <v-btn color="error" depressed @click="deleteCellConfirm" :loading="isDeleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="imageModalOpen" max-width="900" transition="fade-transition">
      <v-card color="transparent" flat class="d-flex justify-center align-center">
        <!-- Clicking the large image will also close the modal -->
        <v-img 
          :src="selectedImageUrl" 
          contain 
          max-height="85vh" 
          @click="imageModalOpen = false"
          style="cursor: zoom-out; border-radius: 4px; box-shadow: 0 4px 20px rgba(0,0,0,0.5);"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </v-card>
    </v-dialog>
    <v-dialog v-model="datamapperDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <project-norms-data-mapper 
        v-if="datamapperDialog" 
        :country-id="activeDatamapperCountryId" 
        @close="datamapperDialog = false" 
      />
    </v-dialog>

  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from '@vue/composition-api'
import { requiredArray, required } from '@core/utils/validation'
import store from '@/store'
import { formatOptions } from '@core/utils'
import useProjectView from '../useProjectView'
import ProjectNormsDataMapper from './ProjectNormsDataMapper.vue' 
import { 
  mdiCartCheck, mdiEarth, mdiLayersOutline, mdiPencil,
  mdiChartPie, mdiContentCopy, mdiFormatListNumbered, 
  mdiPackageVariant, mdiPlus, mdiDelete, mdiDatabaseSync
} from '@mdi/js'

export default {
  components:{
    ProjectNormsDataMapper
  },
  props: {
    projectData: { type: Object, required: true },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { 
      updateLoading,
      packDescProjects,
      fetchPackDescProjects,
    } = useProjectView()
    
    const normsForm = ref(null)
    const projectDataLocal = ref({ countries: [] })
    const studiesLoading = ref(true) 
    const isSaving = ref(false)
    const addingPhaseId = ref(null)

    const imageModalOpen = ref(false)
    const selectedImageUrl = ref('')

    const deleteDialog = ref(false)
    const isDeleting = ref(false)
    const cellToDelete = ref(null)

    const options = ref({ client_objectives: [], pack_change: [], sample_types: [], brand_types: [], retail_channel: [], data_collections: [], findabilities: [], flash_exposures: [], communications: [], project_outcomes: [], product_positions: [] })
    const optionsLoading = ref({ client_objectives: false, pack_change: false, sample_types: false, brand_types: false, retail_channel: false, data_collections: false, findabilities: false, flash_exposures: false, communications: false, project_outcomes: false, product_positions: false })

    const datamapperDialog = ref(false)
    const activeDatamapperCountryId = ref(null)

    const openDatamapper = (countryId) => {
      activeDatamapperCountryId.value = countryId
      datamapperDialog.value = true
    }

    const statusColor = (status) => {
      if(status == 'NEW') return 'error'
      if(status == 'IN_PROGRESS') return 'warning'
      if(status == 'COMPLETED') return 'success'
      return 'primary'
    }


    const fetchLookupData = async (key, dispatchAction) => {
      optionsLoading.value[key] = true
      try {
        const response = await store.dispatch(`app-project/${dispatchAction}`, {})
        options.value[key] = formatOptions(response.data)
      } catch (error) { console.error(`Error fetching ${key}:`, error) } 
      finally { optionsLoading.value[key] = false }
    }

    const init = async () => {
      studiesLoading.value = true
      projectDataLocal.value = { ...props.projectData, countries: [] }
      try {
        await fetchPackDescProjects(props.projectData)
        mapPolynomDataToCountries()
      } catch (error) { console.error("Failed fetching ODB studies:", error) } 
      finally { studiesLoading.value = false }
    }

    const mapPolynomDataToCountries = () => {
      if (!packDescProjects.value || !Array.isArray(packDescProjects.value.countries)) {
        projectDataLocal.value.countries = props.projectData.countries.map(c => ({ ...c, phases: [] }))
        return
      }

      const updatedCountries = props.projectData.countries.map(country => {
        const odbCountry = packDescProjects.value.countries.find(s => s.project_country === country.id)
        
        let phases = []
        if (odbCountry && odbCountry.phases) {
          phases = odbCountry.phases.map(cell => ({
            cell_id: cell.cell_id,
            study_id: cell.study_id,
            cell_name: cell.cell_name,
            cell_status: cell.cell_status,
            design_type: cell.design_type,
            picture_path: cell.picture_path, 
            is_active: cell.is_active,
            db_id_pack: cell.db_id_pack,
            
            pack_change: cell.pack_change || [],
            sample_types: cell.sample_types || [],
            share_of_shelf: cell.share_of_shelf,
            range_tested: cell.range_tested,
            total_products: cell.total_products,
            shelf_size: cell.shelf_size,
            product_position: cell.product_position,
            product_location: cell.product_location || false,
          }))
        }

        return {
          name: country.country_detail.name,
          id: country.id,
          retail_channel: odbCountry ? odbCountry.retail_channel : null,
          brand_types: odbCountry ? odbCountry.brand_types : [],
          client_objectives: odbCountry ? odbCountry.client_objectives : [],
          data_collection: odbCountry ? odbCountry.data_collection : null,
          findability: odbCountry ? odbCountry.findability : null,
          flash_exp: odbCountry ? odbCountry.flash_exp : null,
          communication: odbCountry ? odbCountry.communication : null,
          project_outcome: odbCountry ? odbCountry.project_outcome : null,
          phases
        }
      })

      projectDataLocal.value = {
        id: props.projectData.id,
        job_number: props.projectData.job_number,
        validation:packDescProjects.value.validation,
        countries: updatedCountries
      }
    }

    const revertToInitialData = () => { mapPolynomDataToCountries() }

    const copyFirstPhaseToAll = (country) => {
      if (!country.phases || country.phases.length < 2) return
      const source = country.phases[0]
      const fieldsToSync = ['pack_change', 'sample_types', 'share_of_shelf', 'range_tested', 'shelf_size', 'total_products', 'product_location', 'product_position']

      country.phases = country.phases.map((phase, index) => {
        if (index === 0) return phase
        return {
          ...phase,
          ...fieldsToSync.reduce((acc, field) => {
            acc[field] = Array.isArray(source[field]) ? [...source[field]] : source[field]
            return acc
          }, {})
        }
      })
    }

    const addPhase = async (country) => {
      addingPhaseId.value = country.id
      
      const newPhaseCount = (country.phases?.length || 0) + 1
      const payload = {
        country_id: country.id,
        cell_name: `Test ${newPhaseCount}`,
        cell_status: `NEW`,
        design_type: 'Test'
      }

      try {
        const response = await store.dispatch('app-project/addODBPhase', {
          jobNumber: props.projectData.job_number,
          payload
        })
        
        const newCell = response.data
        // Ensure properties exist for UI components
        newCell.pack_change = newCell.pack_change || []
        newCell.sample_types = newCell.sample_types || []
        newCell.product_location = newCell.product_location || false

        if (!country.phases) {
          vm.$set(country, 'phases', [])
        }
        country.phases.push(newCell)
        
      } catch (error) {
        console.error("Failed to add phase:", error)
      } finally {
        addingPhaseId.value = null
      }
    }

    const confirmDeleteCell = (country, phase) => {
      cellToDelete.value = {
        countryId: country.id,
        studyId: phase.study_id,
        cellId: phase.cell_id,
        cellName: phase.cell_name
      }
      deleteDialog.value = true
    }

    const deleteCellConfirm = async () => {
      if (!cellToDelete.value) return
      isDeleting.value = true
      
      try {
        await store.dispatch('app-project/deleteODBPhase', {
          jobNumber: props.projectData.job_number,
          phaseId: cellToDelete.value.cellId
        })
        
        // Remove locally from UI state to avoid refreshing whole page
        const country = projectDataLocal.value.countries.find(c => c.id === cellToDelete.value.countryId)
        if (country) {
          country.phases = country.phases.filter(p => p.cell_id !== cellToDelete.value.cellId)
        }
        deleteDialog.value = false
      } catch (error) {
        console.error("Failed to delete cell", error)
      } finally {
        isDeleting.value = false
        cellToDelete.value = null
      }
    }

    const onSubmit = async () => {
      if (normsForm.value.validate()) {
        isSaving.value = true
        const payloadToSubmit = JSON.parse(JSON.stringify(projectDataLocal.value))
        
        try {
          await store.dispatch('app-project/updateODBProject', {
            jobNumber: props.projectData.job_number,
            payload: payloadToSubmit
          })
          // Trigger success notification here if you have one
          emit('project-updated', payloadToSubmit)
        } catch (error) {
          console.error("Failed to save project settings:", error)
        } finally {
          isSaving.value = false
        }
      }
    }

    const openImageModal = (url) => {
      if (!url) return
      selectedImageUrl.value = url
      imageModalOpen.value = true
    }
    
    const decipherPhaseRules = (country, currentPhase) => [
      v => {
        // 1. Allow empty (if you want it strictly required, change to: if(!v) return 'Required')
        if (v === null || v === undefined || v === '') return true; 
        
        const num = Number(v);
        if (!Number.isInteger(num)) return 'Must be a whole number';
        
        // 2. Validate Range (Between 1 and Total Phases)
        const totalPhases = country.phases.length;
        if (num < 1 || num > totalPhases) {
          return `Must be between 1 and ${totalPhases}`;
        }

        // 3. Validate Uniqueness within the specific country
        const duplicateCount = country.phases.filter(p => Number(p.db_id_pack) === num).length;
        if (duplicateCount > 1) {
          return 'Number must be unique within this country';
        }

        return true;
      }
    ]

    onMounted(() => {
      init()
      fetchLookupData('client_objectives', 'fetchClientObjs')
      fetchLookupData('pack_change', 'fetchPackChange')
      fetchLookupData('sample_types', 'fetchSampleTypes')
      fetchLookupData('brand_types', 'fetchBrandTypes')
      fetchLookupData('retail_channel', 'fetchRetailChannels')
      fetchLookupData('data_collections', 'fetchODBDataCollections')
      fetchLookupData('findabilities', 'fetchODBFindabilities')
      fetchLookupData('flash_exposures', 'fetchODBFlashExposures')
      fetchLookupData('communications', 'fetchODBCommunications')
      fetchLookupData('project_outcomes', 'fetchODBProjectOutcomes')
      fetchLookupData('product_positions', 'fetchODBProductPositions')
    })

    return {
      projectDataLocal, packDescProjects, studiesLoading, options, optionsLoading, updateLoading, isSaving, addingPhaseId,
      onSubmit, revertToInitialData, copyFirstPhaseToAll, confirmDeleteCell, deleteCellConfirm, addPhase,
      normsForm, deleteDialog, isDeleting, cellToDelete,
      imageModalOpen, selectedImageUrl, openImageModal, statusColor,
      datamapperDialog, activeDatamapperCountryId, openDatamapper,
      icons: { mdiCartCheck, mdiEarth, mdiDatabaseSync, mdiLayersOutline, mdiPencil, mdiChartPie, mdiContentCopy, mdiFormatListNumbered, mdiPackageVariant, mdiPlus, mdiDelete },
      rules: {
        required,
        requiredArray,
        decipherPhaseRules
      }
    
    }
  }
}
</script>

<style lang="scss" scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.phase-card {
  background: white;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
::v-deep .v-input--dense .v-label {
  font-size: 0.85rem;
}
</style>